import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { TextInput, Space, Button, ActionIcon, Flex } from "@mantine/core";
import { shallow } from "zustand/shallow";
import { useTranslation } from "react-i18next";
import { IconArrowRight } from "@tabler/icons-react";
import styled from "styled-components";

import ContestSlider from "./ContestSlider";
import EntrySummary from "./EntrySummary";
import LinkDisplay from "./LinkDisplay";
import entityIcon from "@util/entityIcon";
import getGeoLocation from "@util/getGeoLocation";
import useAppStore from "./microsite-store";
import sendActionEvents, { actionIds } from "@components/EntityActionAssociator/sendActionEvents";

export default function UserEffortView({
  contests = [],
  onSlideChange,
  fetchData,
}) {
  const entityInfo = useAppStore((state) => state.entityInfo);

  const effortConfig = entityInfo.configuration;

  return (
    <div>
      {entityInfo && entityInfo.use_keywords && (
        <React.Fragment>
          <KeywordForm onSuccess={fetchData} />
          <Space h="md" />
        </React.Fragment>
      )}
      <ContestSlider onSlideChange={onSlideChange} contests={contests} />
      {effortConfig && <LinkDisplay links={effortConfig.links} />}
    </div>
  );
}

const KeywordForm = ({ onSuccess }) => {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const { t } = useTranslation();

  const { coords, entityInfo, user } = useAppStore(
    (state) => ({
      coords: state.coords,
      entityInfo: state.entityInfo,
      user: state.user,
    }),
    shallow
  );

  if (!entityInfo.campaign_effort_id) return null;

  function onSubmit() {
    if (!value) {
      return;
    }

    const req = {
      keyword: value.trim(),
      campaign_effort_id: entityInfo.campaign_effort_id,
      user_id: user.id,
    };

    if (coords) {
      req.latitude = coords.latitude;
      req.longitude = coords.longitude;
    }

    if (entityInfo.location) {
      req.location_id = entityInfo.location.id;
    }

    sendActionEvents({
      action_id: actionIds.KEYWORD_SUBMIT,
      events: entityInfo?.paid_media_settings?.events || [],
    });

    setLoading(true);

    getGeoLocation().then((newCoords) => {
      if (newCoords) {
        req.latitude = newCoords.latitude;
        req.longitude = newCoords.longitude;
      }

      axios
        .post(`/microsite/keyword-submit/`, req)
        .then(({ data }) => {
          setLoading(false);
          sendActionEvents({
            action_id: actionIds.KEYWORD_REDEEM,
            events: entityInfo?.paid_media_settings?.events || [],
          });
          if (data.response[0]) {
            setSummary(data.response[0]);
          } else {
            toast.success(t("keyword_entry_success"));
            onSuccess();
            setValue("");
          }
        })
        .catch((err) => {
          setLoading(false);
          toast.error(err);
        });
    });
  }

  return (
    <div>
      {summary && (
        <EntrySummary
          keyword={value}
          results={summary}
          onClose={() => {
            setValue("");
            onSuccess();
            setSummary(null);
          }}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!value) return;
          onSubmit();
        }}
      >
        <Flex>
          <StyledInput>
            <TextInput
              placeholder={t("keyword_input_placeholder")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              size="lg"
              styles={{
                input: {
                  textAlign: "center",
                },
              }}
            />
          </StyledInput>
          <ActionIcon
            size="xl"
            disabled={loading}
            onClick={() => {
              if (!value) return;
              onSubmit();
            }}
            // color="dark"
            loading={loading}
            style={{
              height: "var(--input-height-lg)",
              borderRadius:
                "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
            }}
          >
            <IconArrowRight size={28} />
          </ActionIcon>
        </Flex>
      </form>
      {/* <Button
        leftSection={entityIcon.keyword(24)}
        disabled={loading}
        onClick={onSubmit}
        loading={loading}
        mt="sm"
        size="lg"
        fullWidth
      >
        {t("keyword_submit_button")}
      </Button> */}
    </div>
  );
};

const StyledInput = styled.div`
  flex-grow: 1;
  input {
    background-color: var(--mantine-color-gray-3);
    border-radius: var(--mantine-radius-md) 0 0 var(--mantine-radius-md);
  }

  input::placeholder {
    color: var(--mantine-color-gray-6);
  }
`;
